// Secciones del editor de un código: las usa el alta y, deshabilitadas, el detalle de uno creado.
import type { ReactNode } from 'react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { EditorField } from '@/shared/patterns/EditorForm';
import { LifecycleEditorLayout } from '@/shared/patterns/Lifecycle';
import { ValidityCalendars } from '@/shared/patterns/ValidityCalendars';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import { DISCOUNT_CODE_HELP } from '../constants/helpText';
import type { DiscountCodeFormValues } from '../lib/discountCodeForm';
import { DISCOUNT_TYPE_OPTIONS } from '../lib/salePromotionFormOptions';
import { FIELD_INPUT, FieldMessage, STRIP_INPUT } from './editorParts';
import { PaymentOptionsPicker } from './PaymentOptionsPicker';
import { SkuScopeGroup, StoreScopeGroup } from './ScopePickers';

export interface DiscountCodeFieldsProps {
    form: UseFormReturn<DiscountCodeFormValues>;
    sources: Pick<PromotionFormSources, 'products' | 'productsState' | 'stores' | 'storesState'>;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    /** De dónde salió: «Admin VL» en el alta, la venta en un cupón. */
    origin: string;
    /** Calendarios de solo lectura; los demás campos los apaga el `fieldset` de quien lo monta. */
    readOnly?: boolean;
    /** Alcance de tiendas de un cupón, que los selectores no representan. */
    storesText?: string;
    /** Datos de solo lectura al final de Reglas: usos y dónde se canjeó. */
    rulesExtra?: ReactNode;
    /** Etiqueta del texto: «Cupón» en un cupón emitido. */
    codeLabel?: string;
}

/** Un dato que no se edita, en el lugar del control. */
export function ReadOnlyValue({ children }: { children: ReactNode }) {
    return <div className="py-1.5 text-xs text-foreground">{children}</div>;
}

export function DiscountCodeFields({
    form,
    sources,
    today,
    origin,
    readOnly = false,
    storesText,
    rulesExtra,
    codeLabel = 'Código',
}: DiscountCodeFieldsProps) {
    const {
        register,
        control,
        setValue,
        formState: { errors },
    } = form;
    const [discountType, validFrom, validUntil] = useWatch({
        control,
        name: ['discountType', 'validFrom', 'validUntil'],
    });

    return (
        <LifecycleEditorLayout
            help={{
                description: DISCOUNT_CODE_HELP.description,
                levers: DISCOUNT_CODE_HELP.levers,
                scope: DISCOUNT_CODE_HELP.scope,
                rules: DISCOUNT_CODE_HELP.rules,
            }}
            description={
                <>
                    <EditorField
                        label={codeLabel}
                        help={DISCOUNT_CODE_HELP.codeText}
                        error={errors.codeText?.message}
                        className="mb-2.5"
                    >
                        {(id) => (
                            <input
                                id={id}
                                type="text"
                                autoCapitalize="characters"
                                placeholder="ej. VERANO500"
                                aria-invalid={errors.codeText ? true : undefined}
                                className={`${FIELD_INPUT} font-mono`}
                                {...register('codeText')}
                            />
                        )}
                    </EditorField>
                    <p className="text-[10.5px] text-muted-foreground">Origen: {origin}</p>
                </>
            }
            levers={
                <div className="grid grid-cols-2 gap-3">
                    <EditorField label="Tipo de descuento" help={DISCOUNT_CODE_HELP.discountType}>
                        {(id) => (
                            <select id={id} className={FIELD_INPUT} {...register('discountType')}>
                                {DISCOUNT_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </EditorField>
                    <EditorField
                        label={discountType === 'PERCENTAGE' ? 'Porcentaje (%)' : 'Monto ($)'}
                        help={DISCOUNT_CODE_HELP.discountValue}
                        error={errors.discountValue?.message}
                    >
                        {(id) => (
                            <input
                                id={id}
                                type="text"
                                inputMode="decimal"
                                aria-invalid={errors.discountValue ? true : undefined}
                                className={FIELD_INPUT}
                                {...register('discountValue')}
                            />
                        )}
                    </EditorField>
                </div>
            }
            scope={
                <>
                    <div className="grid grid-cols-2 gap-2.5">
                        <Controller
                            control={control}
                            name="skus"
                            render={({ field }) => (
                                <SkuScopeGroup
                                    withAllChip
                                    readOnly={readOnly}
                                    title="Equipos"
                                    searchLabel="Buscar equipo del código"
                                    products={sources.products}
                                    productsState={sources.productsState}
                                    selected={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        {storesText === undefined ? (
                            <Controller
                                control={control}
                                name="storeIds"
                                render={({ field }) => (
                                    <StoreScopeGroup
                                        withAllChip
                                        readOnly={readOnly}
                                        title="Tiendas"
                                        searchLabel="Buscar tienda del código"
                                        stores={sources.stores}
                                        storesState={sources.storesState}
                                        selected={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        ) : (
                            <EditorField label="Tiendas" help={DISCOUNT_CODE_HELP.couponScope}>
                                <ReadOnlyValue>{storesText}</ReadOnlyValue>
                            </EditorField>
                        )}
                    </div>
                    <EditorField
                        label="Forma de pago"
                        help={DISCOUNT_CODE_HELP.paymentOptions}
                        className="mt-3"
                    >
                        <Controller
                            control={control}
                            name="paymentOptions"
                            render={({ field }) => (
                                <PaymentOptionsPicker
                                    selected={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </EditorField>
                </>
            }
            rules={
                <div className="grid grid-cols-2 gap-3">
                    <EditorField
                        label="Usos máximos"
                        help={DISCOUNT_CODE_HELP.maxUses}
                        error={errors.maxUses?.message}
                    >
                        {(id) => (
                            <input
                                id={id}
                                type="text"
                                inputMode="numeric"
                                placeholder="Sin tope"
                                aria-invalid={errors.maxUses ? true : undefined}
                                className={STRIP_INPUT}
                                {...register('maxUses')}
                            />
                        )}
                    </EditorField>
                    <EditorField label="Anuncio" help={DISCOUNT_CODE_HELP.announceInStore}>
                        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11.5px]">
                            <input
                                type="checkbox"
                                className="accent-primary"
                                {...register('announceInStore')}
                            />
                            Se anuncia en tienda
                        </label>
                    </EditorField>
                    {rulesExtra}
                </div>
            }
            validity={
                <>
                    <ValidityCalendars
                        validFrom={validFrom === '' ? null : validFrom}
                        validTo={validUntil === '' ? null : validUntil}
                        today={today}
                        disabled={readOnly}
                        onChange={({ validFrom: from, validTo }) => {
                            setValue('validFrom', from ?? '', { shouldDirty: true });
                            setValue('validUntil', validTo ?? '', { shouldDirty: true });
                        }}
                    />
                    <FieldMessage message={errors.validUntil?.message} />
                    <p className="mt-1.5 text-[10.5px] text-muted-foreground">
                        Los días cuentan en hora de Ciudad de México.
                    </p>
                </>
            }
        />
    );
}
